// B-07 — Section "Parties" sur l'onglet Résumé d'un dossier.
// Groupage par rôle, autocomplete sur contacts existants, création inline.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Star, Trash2, User, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import {
  useDossierContacts,
  useLinkContactToDossier,
  useUnlinkContactFromDossier,
  useContacts,
} from '../../hooks/queries/useContacts';
import {
  type Contact,
  type DossierContactRole,
  DOSSIER_CONTACT_ROLE_LABELS,
  getContactDisplayName,
} from '../../services/contacts/contactService';
import { ContactEditModal } from '../contacts/ContactEditModal';
import { InviteClientButton } from '../portal/InviteClientButton';

export const ROLE_ORDER: DossierContactRole[] = [
  'client', 'local_counsel', 'opposing_party', 'opposing_counsel',
  'court', 'bailiff',
  'judicial_representative', 'judicial_administrator',
  'expert', 'other',
];

interface Props {
  dossierId: string;
  canEdit: boolean;
}

export function DossierPartiesSection({ dossierId, canEdit }: Props) {
  const { data: links, isLoading } = useDossierContacts(dossierId);
  const unlink = useUnlinkContactFromDossier();
  const [addOpen, setAddOpen] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<DossierContactRole, typeof links>();
    for (const role of ROLE_ORDER) map.set(role, []);
    (links ?? []).forEach((l) => {
      const arr = map.get(l.role) ?? [];
      arr.push(l);
      map.set(l.role, arr);
    });
    return map;
  }, [links]);

  return (
    <section className="bg-white border border-neutral-200 rounded-cime overflow-hidden">
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-100 bg-neutral-50">
        <h3 className="text-sm font-semibold text-cime-text-primary">
          Parties
          {links && links.length > 0 && (
            <span className="ml-1.5 text-xs font-normal text-cime-text-muted">
              ({links.length})
            </span>
          )}
        </h3>
        {canEdit && (
          <button
            onClick={() => setAddOpen(true)}
            className="text-cime-text-muted hover:text-cime-primary"
            aria-label="Ajouter une partie"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </header>

      <div className="p-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4"><Spinner /></div>
        ) : !links || links.length === 0 ? (
          <p className="text-sm italic text-cime-text-muted">
            Aucune partie liée. {canEdit && 'Cliquez sur + pour ajouter un contact.'}
          </p>
        ) : (
          ROLE_ORDER.map((role) => {
            const arr = grouped.get(role) ?? [];
            if (arr.length === 0) return null;
            return (
              <div key={role}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
                  {DOSSIER_CONTACT_ROLE_LABELS[role]} ({arr.length})
                </p>
                <ul className="space-y-1">
                  {arr.map((link) => (
                    <li key={`${link.contact_id}-${link.role}`}>
                      <PartyRow
                        contact={link.contact}
                        contactId={link.contact_id}
                        role={link.role}
                        isPrimary={link.is_primary}
                        canEdit={canEdit}
                        onRemove={() => {
                          if (window.confirm(`Retirer ${getContactDisplayName(link.contact)} de ce dossier ?`)) {
                            unlink.mutate({
                              dossierId,
                              contactId: link.contact_id,
                              role: link.role,
                            });
                          }
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>

      {addOpen && (
        <AddPartyModal
          dossierId={dossierId}
          existingIds={new Set((links ?? []).map((l) => `${l.contact_id}-${l.role}`))}
          onClose={() => setAddOpen(false)}
        />
      )}
    </section>
  );
}

// ─── Ligne : nom + bouton retirer ────────────────────────────────────────────

function PartyRow({
  contact, contactId, role, isPrimary, canEdit, onRemove,
}: {
  contact: Contact;
  contactId: string;
  role: DossierContactRole;
  isPrimary: boolean;
  canEdit: boolean;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-1 group">
      <Link
        to={`/contacts/${contact.id}`}
        className="flex items-center gap-2 flex-1 min-w-0 text-sm hover:text-cime-primary transition"
      >
        {contact.kind === 'corporate' ? (
          <Building2 className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
        ) : (
          <User className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
        )}
        <span className="truncate">{getContactDisplayName(contact)}</span>
        {isPrimary && (
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" aria-label="Partie principale" />
        )}
      </Link>
      {role === 'client' && <InviteClientButton contactId={contactId} compact />}
      {canEdit && (
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 transition text-neutral-400 hover:text-red-600"
          aria-label="Retirer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── Modale "Ajouter une partie" ─────────────────────────────────────────────

export function AddPartyModal({
  dossierId, existingIds, onClose, defaultRole = 'client',
}: {
  dossierId: string;
  existingIds: Set<string>;     // "contactId-role" déjà présents
  onClose: () => void;
  /** Rôle présélectionné selon le bouton d'ajout cliqué. */
  defaultRole?: DossierContactRole;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Contact | null>(null);
  const [role, setRole] = useState<DossierContactRole>(defaultRole);
  const [isPrimary, setIsPrimary] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const link = useLinkContactToDossier();
  const { data: contacts, isFetching } = useContacts({
    search: search.trim() || undefined,
  });

  const candidates = useMemo(
    () => (contacts ?? []).slice(0, 20),
    [contacts],
  );

  function submit() {
    if (!selected) return;
    link.mutate(
      { dossierId, contactId: selected.id, role, isPrimary },
      { onSuccess: onClose },
    );
  }

  // Si on ouvre la création, on revient ensuite et on sélectionne le contact créé
  if (createOpen) {
    return (
      <ContactEditModal
        defaultCategory={role === 'opposing_party' ? 'opposing_party' : 'client'}
        onClose={() => setCreateOpen(false)}
        onSaved={(c) => {
          setSelected(c);
          setSearch(getContactDisplayName(c));
          setCreateOpen(false);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-cime shadow-xl w-full max-w-md overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 className="font-semibold text-cime-text-primary">Ajouter une partie</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Search */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Rechercher un contact
            </label>
            <Input
              autoFocus
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (selected) setSelected(null);
              }}
              placeholder="Nom, email…"
            />
          </div>

          {/* Créer un contact — toujours visible tant qu'aucun n'est sélectionné */}
          {!selected && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md border border-dashed border-cime-primary/50 text-cime-primary hover:bg-cime-primary/5 transition"
            >
              <Plus className="w-3.5 h-3.5" /> Créer un nouveau contact
            </button>
          )}

          {/* Liste candidats */}
          {!selected && search.trim().length > 0 && (
            <div className="border border-neutral-200 rounded-md max-h-60 overflow-y-auto">
              {isFetching && candidates.length === 0 ? (
                <p className="text-sm text-neutral-400 px-3 py-3 text-center">Recherche…</p>
              ) : candidates.length === 0 ? (
                <p className="text-sm italic text-neutral-400 px-3 py-3 text-center">
                  Aucun contact trouvé.
                </p>
              ) : (
                candidates.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setSelected(c); setSearch(getContactDisplayName(c)); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-neutral-50"
                  >
                    {c.kind === 'corporate' ? (
                      <Building2 className="w-3.5 h-3.5 text-neutral-400" />
                    ) : (
                      <User className="w-3.5 h-3.5 text-neutral-400" />
                    )}
                    <span className="flex-1 truncate">{getContactDisplayName(c)}</span>
                    {c.email && (
                      <span className="text-xs text-cime-text-muted truncate">{c.email}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {selected && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-sm flex items-center gap-2">
              <span className="text-emerald-700">✓ Sélectionné :</span>
              <strong className="text-emerald-900">{getContactDisplayName(selected)}</strong>
              <button
                type="button"
                onClick={() => { setSelected(null); setSearch(''); }}
                className="ml-auto text-emerald-700 hover:text-emerald-900"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Rôle */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Rôle dans le dossier
            </label>
            <div className="grid grid-cols-3 auto-rows-fr gap-1.5">
              {ROLE_ORDER.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={cn(
                    'flex h-full min-h-12 items-center justify-center px-2 py-1.5 text-center text-xs leading-snug rounded-md border transition',
                    role === r
                      ? 'bg-cime-primary text-white border-cime-primary'
                      : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                  )}
                >
                  {DOSSIER_CONTACT_ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Primary */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="w-4 h-4"
            />
            Partie principale (marquée d'une étoile dans la liste)
          </label>

          {/* Doublon ? */}
          {selected && existingIds.has(`${selected.id}-${role}`) && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
              Ce contact est déjà lié à ce dossier avec ce rôle.
            </div>
          )}

          {link.isError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5">
              {link.error instanceof Error ? link.error.message : 'Erreur.'}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" onClick={onClose} disabled={link.isPending}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!selected || link.isPending || existingIds.has(`${selected?.id}-${role}`)}
          >
            {link.isPending ? 'Ajout…' : 'Ajouter au dossier'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
